import type { SavedSearch } from '@/types/auth';
import { availabilityOf, isBuyable, type Availability } from './availability';
import type { Listing, SearchParams } from '@/types/listing';
import { savedSearchToFilters } from './savedSearch';
import {
  citySlugsFromFilters,
  filtersToApiParams,
  filtersToQueryString,
  searchFiltersSchema,
  type SearchFilters,
} from './searchParams';
import { daysSince } from './savedSearchVisits';
import type { RecentSearch } from './recentSearches';
import { formatPrice, titleCaseCity } from './utils';
import type { SeenPrices } from './favoriteSeen';
import {
  hasCredibleCut,
  MAX_CREDIBLE_CUT_SHARE,
  MIN_CREDIBLE_PRICE,
  priceCutAmount,
  weekendWindow,
} from './homeFeed';

/**
 * The signed-in half of the curated home feed.
 *
 * The guest rails (`lib/homeFeed`) are server-rendered, so they ship in the HTML
 * and carry the LCP image. Everything here needs the buyer's own account data,
 * which means an authenticated client fetch after the session resolves — so the
 * rules are different and worth stating:
 *
 *  - **Nothing here may delay the guest feed.** These rails hydrate in below the
 *    first server rail; the LCP element (the h1) and the first rail's photos are
 *    already painted by then.
 *  - **A rail with nothing real to say does not render.** No empty shells: a
 *    signed-in buyer with no saved searches sees the guest rails, which is the
 *    documented fallback, not a row of placeholder cards.
 *  - **Every count is a live count.** "3 new" is `total` from a real search, and
 *    the "See all" link carries the same filters, so the rail and its destination
 *    cannot disagree.
 *
 * (W14 R133+)
 */

/**
 * How many saved searches the home page is willing to ask about.
 *
 * Each one costs a search request, and the page only shows the best of them, so
 * the fan-out is bounded rather than proportional to how organised the buyer is.
 * Matches the dashboard's cap.
 */
export const MAX_SAVED_SEARCH_PROBES = 4;

/** Cards in a personalized rail. */
export const PERSONAL_RAIL_CARDS = 12;

/**
 * A personalized rail is allowed to be **one card**, unlike a curated one
 * (`homeFeed.MIN_RAIL_CARDS` = 3).
 *
 * The reason a lonely curated card reads as a bug is that nothing on screen
 * explains why there is only one — "Open houses this weekend" with a single
 * home looks like a failed fetch. Here the count *is* the headline: "1 new since
 * you last looked" is a complete, useful statement, and suppressing it would
 * hide the one thing the buyer came back to find out.
 */
export const MIN_PERSONAL_RAIL_CARDS = 1;

/* ------------------------------------------------------------------ *
 * New matches on a saved search
 * ------------------------------------------------------------------ */

export interface NewMatchQuery {
  id: string;
  name: string;
  /** The window asked of the API: listed within N days (days since last visit). */
  days: number;
  /** Whether the baseline is a real visit or the day the search was saved. */
  baseline: 'visit' | 'saved';
  params: SearchParams;
  /** A `/homes` URL that lands on exactly the homes this rail is showing. */
  href: string;
}

/**
 * One search request per saved search: "homes matching these filters, listed
 * since the buyer last opened this search".
 *
 * The API has no per-user visit tracking, so the baseline comes from
 * `savedSearchVisits` (localStorage, per user) and falls back to when the search
 * was created — which makes the first badge honestly "new since you saved this
 * search" rather than a number nobody can account for. `dom_max` (days on
 * market) is the API's expression of that window.
 */
export function newMatchQueries(
  searches: SavedSearch[],
  lastVisit: (searchId: string) => number | null,
  now: number
): NewMatchQuery[] {
  return searches.slice(0, MAX_SAVED_SEARCH_PROBES).map((s) => {
    const filters = savedSearchToFilters(s.filters);
    const visited = lastVisit(s.id);
    const saved = Date.parse(s.created_at);
    // A saved search with an unparseable created_at would otherwise produce
    // `daysSince(NaN)` → NaN → an invalid dom_max. Fall back to "today".
    const baselineMs = visited ?? (Number.isFinite(saved) ? saved : now);
    const withWindow = {
      ...filters,
      domMax: daysSince(baselineMs, now),
      sort: 'newest' as const,
      page: 0,
    };
    return {
      id: s.id,
      name: s.name,
      days: withWindow.domMax,
      baseline: visited !== null ? ('visit' as const) : ('saved' as const),
      params: { ...filtersToApiParams(withWindow), perPage: PERSONAL_RAIL_CARDS },
      href: `/homes${filtersToQueryString(withWindow)}`,
    };
  });
}

export interface NewMatchResult extends NewMatchQuery {
  /** Live total matching the window — the number in the heading. */
  total: number;
  listings: Listing[];
}

/**
 * The saved search with the most new homes gets the rail; ties keep API order
 * (most recently saved first, as the endpoint returns them).
 *
 * One rail rather than one per search, because four rails of two cards each is
 * noise — and because a single rail can carry a single honest "See all" URL.
 * Searches that also have news are named in a one-line footnote instead.
 */
export function pickNewMatchRail(results: (NewMatchResult | null)[]): NewMatchResult | null {
  let best: NewMatchResult | null = null;
  for (const r of results) {
    if (!r || r.listings.length < MIN_PERSONAL_RAIL_CARDS) continue;
    if (!best || r.total > best.total) best = r;
  }
  return best;
}

/** Saved searches other than the chosen one that also have new matches. */
export function otherSearchesWithNews(
  results: (NewMatchResult | null)[],
  chosenId: string | undefined
): NewMatchResult[] {
  return results.filter(
    (r): r is NewMatchResult => !!r && r.id !== chosenId && r.total > 0
  );
}

/* ------------------------------------------------------------------ *
 * Saved homes, with news
 * ------------------------------------------------------------------ */

/**
 * Something that has actually happened to a home the buyer saved.
 *
 * Three notes, each a fact with a number or a date behind it:
 *  - `drop` — it is cheaper than the price we recorded on a date we can name.
 *    The only observation of "changed while you were away" available, because the
 *    API has no price history (see `lib/favoriteSeen`).
 *  - `below-original` — it is priced under its original MLS ask. Straight off the
 *    record, so it works on a buyer's very first visit, when there is no local
 *    observation yet.
 *  - `open-house` — a dated, timed open house that has not happened yet.
 */
export type FavoriteNote =
  | { kind: 'drop'; amount: number; pct: number; sinceMs: number }
  | { kind: 'below-original'; amount: number; pct: number }
  | { kind: 'open-house'; date: string; start: string; end: string; thisWeekend: boolean }
  /** The home left the market. Outranks and **replaces** every other note. */
  | { kind: 'unavailable'; availability: Availability; changed?: string };

export interface FavoriteNews {
  listing: Listing;
  /** Most newsworthy first; never empty (a home with no notes is not included). */
  notes: FavoriteNote[];
}

/** How many saved homes we're willing to hydrate for the rail. */
export const MAX_FAVORITES_PROBED = 30;

/** Cheapest upcoming open house on this listing, or null. */
function nextOpenHouse(l: Listing, todayISO: string) {
  const upcoming = (l.openHouses ?? [])
    .map((oh) => ({ ...oh, day: (oh.date ?? '').slice(0, 10) }))
    .filter((oh) => oh.day >= todayISO)
    .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
  return upcoming[0] ?? null;
}

/**
 * The saved homes with something to say, most newsworthy first.
 *
 * A home with nothing to report is **left out entirely** rather than padding the
 * rail: the heading promises what changed, so every card under it has to have
 * changed. If that leaves nothing, the caller renders no rail at all.
 *
 * Credibility bounds are shared with the curated price-cut rail — a favorite
 * whose record says "90% off" is a dropped digit there and here alike, and a card
 * shouting a data error at the buyer about *their own* saved home is worse, not
 * better, than one in a market rail.
 */
export function favoriteNews(
  listings: Listing[],
  seen: SeenPrices,
  todayISO: string,
  limit = PERSONAL_RAIL_CARDS
): FavoriteNews[] {
  const { from, to } = weekendWindow(todayISO);

  const scored = listings.map((l) => {
    const notes: FavoriteNote[] = [];

    /**
     * A home that can no longer be bought gets exactly one note, and it is that.
     *
     * Every other note on this rail is a reason to act — a price drop, a cut
     * against the original ask, an open house this weekend. Attaching any of them
     * to an expired listing tells a buyer to go and see a house that is not for
     * sale: an open-house row outlives the listing in the feed, so *"Open house
     * this Sat, 2–4pm"* on a home the MLS stopped confirming is the worst thing
     * this rail could say. And the fact itself is news the buyer wants — they
     * saved this home — so it is reported rather than dropped. (W16 R156)
     */
    const availability = availabilityOf(l.status);
    if (!isBuyable(availability)) {
      return {
        listing: l,
        notes: [
          { kind: 'unavailable', availability, changed: l.statusChangeDate } as FavoriteNote,
        ],
      };
    }

    const before = seen[l.id];
    if (before && before.price > l.price && l.price >= MIN_CREDIBLE_PRICE) {
      const amount = before.price - l.price;
      const pct = amount / before.price;
      if (pct <= MAX_CREDIBLE_CUT_SHARE) {
        notes.push({ kind: 'drop', amount, pct, sinceMs: before.at });
      }
    }

    if (hasCredibleCut(l)) {
      const amount = priceCutAmount(l);
      notes.push({
        kind: 'below-original',
        amount,
        pct: amount / (l.originalListPrice ?? 1),
      });
    }

    const oh = nextOpenHouse(l, todayISO);
    if (oh) {
      notes.push({
        kind: 'open-house',
        date: oh.day,
        start: oh.start_time,
        end: oh.end_time,
        thisWeekend: oh.day >= from && oh.day <= to,
      });
    }

    return { listing: l, notes };
  });

  return scored
    .filter((s) => s.notes.length > 0)
    .sort((a, b) => newsRank(b) - newsRank(a))
    .slice(0, limit);
}

/** A drop we observed outranks a weekend open house, which outranks the rest. */
function newsRank(n: FavoriteNews): number {
  let rank = 0;
  for (const note of n.notes) {
    // A home that has gone is the most important thing this rail can report.
    if (note.kind === 'unavailable') rank += 500;
    else if (note.kind === 'drop') rank += 100;
    else if (note.kind === 'open-house') rank += note.thisWeekend ? 50 : 10;
    else rank += 20;
  }
  return rank;
}

/* ------------------------------------------------------------------ *
 * Picked for you
 * ------------------------------------------------------------------ */

/**
 * What this buyer has actually told us they want.
 *
 * Two sources, in order of how deliberate they are: a **saved search** (they
 * typed a name and pressed save) beats a **recent search** (they looked at a
 * city once). Nothing is inferred beyond that — no lookalike modelling, no
 * "buyers like you", because there is no data behind either and the subtitle has
 * to be able to say where the rail came from in one honest line.
 *
 * `null` when there is no signal at all, which is the documented fallback: the
 * guest rails are already on the page and they are a complete page.
 */
export interface PersonalSignal {
  filters: SearchFilters;
  /** Where it came from, named: a saved search's name or a searched place. */
  label: string;
  source: 'saved-search' | 'recent-search';
  /** The filters in words, for the subtitle. */
  terms: string[];
  /** A `/homes` URL applying exactly these filters. */
  href: string;
}

function signalTerms(f: SearchFilters): string[] {
  const terms: string[] = [];
  for (const slug of citySlugsFromFilters(f)) terms.push(titleCaseCity(slug.replace(/-/g, ' ')));
  if (f.zips) terms.push(f.zips.split(',')[0]);
  if (f.bedsMin !== undefined) terms.push(`${f.bedsMin}+ beds`);
  if (f.priceMax !== undefined) terms.push(`up to ${formatPrice(f.priceMax)}`);
  if (f.priceMin !== undefined && f.priceMax === undefined) {
    terms.push(`${formatPrice(f.priceMin)}+`);
  }
  return terms;
}

/** Is there enough here to make a rail that means something? */
function isUsableSignal(f: SearchFilters): boolean {
  return (
    citySlugsFromFilters(f).length > 0 ||
    !!f.zips ||
    f.bedsMin !== undefined ||
    f.priceMax !== undefined
  );
}

export function deriveSignal(
  savedSearches: SavedSearch[],
  recents: RecentSearch[]
): PersonalSignal | null {
  for (const s of savedSearches) {
    const filters = savedSearchToFilters(s.filters);
    if (!isUsableSignal(filters)) continue;
    const base = { ...filters, sort: 'newest' as const, page: 0 };
    return {
      filters: base,
      label: s.name,
      source: 'saved-search',
      terms: signalTerms(base),
      href: `/homes${filtersToQueryString(base)}`,
    };
  }

  for (const r of recents) {
    // Only a place-shaped search is a preference; a one-off free-text query is
    // not something to build a rail out of.
    if (r.kind !== 'city' && r.kind !== 'zip') continue;
    let params: URLSearchParams;
    try {
      params = new URL(r.href, 'https://goreal.local').searchParams;
    } catch {
      continue;
    }
    const filters = searchFiltersSchema.parse({
      cities: params.get('cities') ?? undefined,
      zips: params.get('zips') ?? undefined,
      // Recorded alongside the place when the buyer had them applied — the
      // recent-searches store keeps the *place* and, since W14 R136, the band.
      bedsMin: r.bedsMin,
      priceMax: r.priceMax,
      sort: 'newest',
    });
    if (!isUsableSignal(filters)) continue;
    return {
      filters,
      label: r.label,
      source: 'recent-search',
      terms: signalTerms(filters),
      href: `/homes${filtersToQueryString(filters)}`,
    };
  }

  return null;
}

/** Over-fetch so excluding what's already on the page can't starve the rail. */
export const PICKED_FETCH_SIZE = 30;

/**
 * Homes matching the signal that the buyer has not already been shown on this
 * page — the new-match rail's cards and their own saved homes are both above
 * this one, and repeating them would make the feed look shorter than it is.
 */
export function pickedForYou(
  listings: Listing[],
  exclude: Set<string>,
  limit = PERSONAL_RAIL_CARDS
): Listing[] {
  return listings.filter((l) => !exclude.has(l.id)).slice(0, limit);
}
